import { computed, reactive, toValue } from "vue";
import { defu } from "defu";
import { hash } from "ohash";
import {
  useAsyncData,
  useRuntimeConfig,
  useState
} from "#app";
import {
  createUser as sdkCreateUser,
  createUsers as sdkCreateUsers,
  readMe as sdkReadMe,
  readUser as sdkReadUser,
  readUsers as sdkReadUsers,
  updateMe as sdkUpdateMe,
  updateUser as sdkUpdateUser,
  updateUsers as sdkUpdateUsers,
  deleteUser as sdkDeleteUser,
  deleteUsers as sdkDeleteUsers
} from "@directus/sdk";
import {
  useDirectusRest,
  useDirectusTokens
} from "#imports";
export function useDirectusUsers(config) {
  const {
    authConfig: {
      userStateName
    },
    moduleConfig: {
      readMeQuery
    }
  } = useRuntimeConfig().public.directus;
  const defaultConfig = {
    staticToken: false
  };
  const client = useDirectusRest(defu(config, defaultConfig));
  const { tokens } = useDirectusTokens(config?.staticToken ?? defaultConfig.staticToken);
  async function createUser(userInfo, query) {
    return await client.request(sdkCreateUser(userInfo, query));
  }
  async function createUsers(userInfo, query) {
    return await client.request(sdkCreateUsers(userInfo, query));
  }
  function setUser(value) {
    return new Promise((resolve) => {
      user.value = value;
      resolve();
    });
  }
  async function readMe(_query) {
    if (tokens.value?.access_token) {
      const { updateState, ...query } = defu(_query, readMeQuery);
      try {
        const userData = await client.request(sdkReadMe(query));
        if (userData && updateState !== false) {
          await setUser(userData);
        }
        return userData;
      } catch (error) {
        if (error && error.message) {
          console.error("Couldn't fetch authenticated user:", error.message);
        } else {
          console.error(error);
        }
      }
    }
  }
  async function readUser(id, query) {
    return await client.request(sdkReadUser(id, query));
  }
  async function readAsyncUser(id, params) {
    const { key, query, ..._params } = params ?? {};
    const _key = computed(() => {
      return key ?? "D_" + hash(["readUser", toValue(id), toValue(query)]);
    });
    return await useAsyncData(_key.value, () => readUser(toValue(id), reactive(query ?? {})), _params);
  }
  async function readUsers(query) {
    return await client.request(sdkReadUsers(query));
  }
  async function readAsyncUsers(params) {
    const { key, query, ..._params } = params ?? {};
    const _key = computed(() => {
      return key ?? "D_" + hash(["readUsers", toValue(query)]);
    });
    return await useAsyncData(_key.value, () => readUsers(reactive(query ?? {})), _params);
  }
  async function updateMe(userInfo, _query) {
    const { updateState, ...query } = _query;
    return await client.request(sdkUpdateMe(userInfo, query)).then((userData) => {
      if (userData && updateState !== false) {
        setUser(userData);
      }
      return userData;
    });
  }
  async function updateUser(id, userInfo, query) {
    return await client.request(sdkUpdateUser(id, userInfo, query));
  }
  async function updateUsers(ids, userInfo, query) {
    return await client.request(sdkUpdateUsers(ids, userInfo, query));
  }
  async function deleteUser(id) {
    return await client.request(sdkDeleteUser(id));
  }
  async function deleteUsers(ids) {
    return await client.request(sdkDeleteUsers(ids));
  }
  const user = useState(userStateName, () => void 0);
  return {
    client,
    createUser,
    createUsers,
    deleteUser,
    deleteUsers,
    readMe,
    readUser,
    readAsyncUser,
    readUsers,
    readAsyncUsers,
    setUser,
    tokens,
    updateMe,
    updateUser,
    updateUsers,
    user
  };
}
